import { useQuery } from '@tanstack/react-query';
import type { ElasticVehiclesResponse } from '@//app/api/search/vehicles';
import useFilterHistory from '@//data/filter-history/useFilterHistory';
import type { FilterHistoryItem } from '@//data/filter-history/utils';
import { useApiBaseUrl } from '@//providers/MarketSettingsProvider';
import { httpClientGetJson } from '../../httpClient';

interface ValidSearchItem extends FilterHistoryItem {
  hasResults: boolean;
}

interface ValidationResult {
  validSearches: ValidSearchItem[];
  hasValidSearches: boolean;
}

const useValidSearchHistory = (maxSections = 2) => {
  const filterHistory = useFilterHistory();
  const apiBaseUrl = useApiBaseUrl();

  // Create stable queryKey based on search IDs and timestamps
  const queryKey = [
    'validSearchHistory',
    filterHistory.slice(0, maxSections * 2).map((s) => ({ id: s.id, date: s.date })),
    maxSections,
  ];

  const query = useQuery<ValidationResult>({
    queryKey,
    queryFn: async (): Promise<ValidationResult> => {
      if (!filterHistory.length) {
        return {
          validSearches: [],
          hasValidSearches: false,
        };
      }

      // Take up to maxSections * 2 searches to validate (in case some fail)
      const searchesToValidate = filterHistory.slice(0, maxSections * 2);

      // Create parallel validation promises
      const validationPromises = searchesToValidate.map(async (search) => {
        try {
          // Create search params with hits=0 to only get stats (more efficient)
          const params = new URLSearchParams(search.query);
          params.set('hits', '0');

          const data = await httpClientGetJson<ElasticVehiclesResponse>(
            `${apiBaseUrl}/api/search/vehicles?${params.toString()}`,
          );

          const totalHits = data?.stats?.totalHits || 0;
          const hasResults = totalHits > 0;

          return hasResults ? ({ ...search, hasResults: true } as ValidSearchItem) : null;
        } catch (error) {
          console.error(`Error validating search ${search.id}:`, error);
          return null;
        }
      });

      // Execute all validations in parallel
      const results = await Promise.allSettled(validationPromises);

      // Extract successful, valid searches
      const validResults: ValidSearchItem[] = [];

      for (const result of results) {
        if (result.status === 'fulfilled' && result.value !== null) {
          validResults.push(result.value);
          if (validResults.length >= maxSections) break; // Stop once we have enough
        }
      }

      return {
        validSearches: validResults,
        hasValidSearches: validResults.length > 0,
      };
    },
    enabled: filterHistory.length > 0,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });

  return {
    validSearches: query.data?.validSearches || [],
    isLoading: query.isLoading,
    hasValidSearches: query.data?.hasValidSearches || false,
  };
};

export default useValidSearchHistory;
